import { LabelValuePair } from '../interfaces';
import { UIFeatureToggle } from './uiFeatureToggle';

export { UIFeatureToggle };

export enum APIFeatureToggle {
  BypassNonceValidation = 'BypassNonceValidation',
  CaseAdditionalPropertyInFramework = 'CaseAdditionalPropertyInFramework',
  CaseAdditionalPropertyInOpenSearch = 'CaseAdditionalPropertyInOpenSearch',
  CustomerCareGranularSecurityAccess = 'CustomerCareGranularSecurityAccess',
  DisconnectHierarchy = 'DisconnectHierarchy',
  EnableAccessRestrictionToPIIData = 'EnableAccessRestrictionToPIIData',
  EnableAddPlanWhileInSuspension = 'EnableAddPlanWhileInSuspension',
  EnableCoreBillingService = 'EnableCoreBillingService',
  EnableDBSSCouponSupport = 'EnableDBSSCouponSupport',
  EnableFinancialBankingUsageDetails = 'EnableFinancialBankingUsageDetails',
  EnableMultiOfferNonBwsBulk = 'EnableMultiOfferNonBwsBulk',
  EnableSaveCartSecurity = 'EnableSaveCartSecurity',
  EnableTaxInclusiveTotalBreakdown = 'EnableTaxInclusiveTotalBreakdown',
  EnableWorkflowActivityErrorsInCare = 'EnableWorkflowActivityErrorsInCare',
  GroupOrdering = 'GroupOrdering',
  IDomainNotificationEventPublicationUsingSNS = 'IDomainNotificationEventPublicationUsingSNS',
  IsLineItemDisputeAvailable = 'IsLineItemDisputeAvailable',
  OffCycleBypassARM = 'OffCycleBypassARM',
  OffCycleDelaySyncToARM = 'OffCycleDelaySyncToARM',
  EnableConditionalAccessGroupResolution = 'EnableConditionalAccessGroupResolution',
  EnableServiceFeatureDecisions = 'EnableServiceFeatureDecisions',
  OrderContracts = 'OrderContracts'
}

export enum UICustomizationFeatureToggle {
  InvAdvancedOffersWidgetEnabled = 'InvAdvancedOffersWidgetEnabled',
  InvAgenticCopilot = 'InvAgenticCopilot',
  InvCareModuleNameCustomizationEnabled = 'InvCareModuleNameCustomizationEnabled',
  /**
   * When enabled, Customer Care clears every Search Panel field after a
   * customer is opened from search. Off by default, so the last search stays
   * filled in.
   */
  InvClearSearchPanelAfterCustomerRetrieved = 'InvClearSearchPanelAfterCustomerRetrieved',
  /**
   * When enabled for a business unit, Customer Care builds its theme from the
   * primary, secondary, and color-scheme custom values. Off by default, so
   * every business unit keeps the static Customer Care theme.
   */
  InvCustomerCareThemingPilotEnabled = 'InvCustomerCareThemingPilotEnabled',
  /**
   * When enabled, logging out also closes windows opened through Open in New
   * Window (each has its own session). Off by default so those windows stay
   * signed in.
   */
  InvCloseClonedSessionsOnLogoutEnabled = 'InvCloseClonedSessionsOnLogoutEnabled',
  InvCustomerNameOnOrderFlowTitleEnabled = 'InvCustomerNameOnOrderFlowTitleEnabled',
  InvDefaultQuoteViewForOrderTotals = 'InvDefaultQuoteViewForOrderTotals',
  InvDetailedOrderConfirmationEnabled = 'InvDetailedOrderConfirmationEnabled',
  InvDisplayCreditCheckButtonEnabled = 'InvDisplayCreditCheckButtonEnabled',
  InvDisplayEmailAndPrintTransaction = 'InvDisplayEmailAndPrintTransaction',
  InvDisplayOfferAutoRenewLinkEnabled = 'InvDisplayOfferAutoRenewLinkEnabled',
  InvDisplayOptionalAddressOnCreateCustomer = 'InvDisplayOptionalAddressOnCreateCustomer',
  InvDisplayOrderNumberAlongsideOrderId = 'InvDisplayOrderNumberAlongsideOrderId',
  InvDisplayPlanNameInsteadOfServiceOfferName = 'InvDisplayPlanNameInsteadOfServiceOfferName',
  InvDisplayPricingPlanDescriptionEnabled = 'InvDisplayPricingPlanDescriptionEnabled',
  InvEnableCustomerCreationBeforeAttributesNC = 'InvEnableCustomerCreationBeforeAttributesNC',
  InvEnableOrderContract = 'InvEnableOrderContract',
  InvExpandLeftDrawerFiltersOnOfferingsStepByDefault = 'InvExpandLeftDrawerFiltersOnOfferingsStepByDefault',
  InvExpandLeftDrawerNavigationOnDecisionAndAttributesStepByDefault = 'InvExpandLeftDrawerNavigationOnDecisionAndAttributesStepByDefault',
  InvExpandRightDrawerMiniCartOnDecisionAndAttributesStepByDefault = 'InvExpandRightDrawerMiniCartOnDecisionAndAttributesStepByDefault',
  InvExpandRightDrawerMiniCartOnOfferingsStepByDefault = 'InvExpandRightDrawerMiniCartOnOfferingsStepByDefault',
  InvMaskCreditCardNumberEnabled = 'InvMaskCreditCardNumberEnabled',
  InvMultiOfferingOrderFlowEnabled = 'InvMultiOfferingOrderFlowEnabled',
  InvOfferingsGroupsViewEnabled = 'InvOfferingsGroupsViewEnabled',
  InvQuickViewOfferDetailsEnabled = 'InvQuickViewOfferDetailsEnabled',
  InvSaveCartInOrderEnabled = 'InvSaveCartInOrderEnabled',
  /** When enabled, an explicit (manual) logout also stores the user's current
   *  location so they resume there on next login. Idle-timeout always stores it
   *  regardless of this customization. */
  InvSaveLastAccessUrlOnManualLogoutEnabled = 'InvSaveLastAccessUrlOnManualLogoutEnabled',
  InvSimpleOfferBasedOrderingEnabled = 'InvSimpleOfferBasedOrderingEnabled',
  InvSalesPipelineEnabled = 'InvSalesPipelineEnabled',
  InvSuppressDifferentInventoryTypePlanSwapWarning = 'InvSuppressDifferentInventoryTypePlanSwapWarning'
}

export enum UICustomizationValue {
  InvCustomerCareThemeColorScheme = 'InvCustomerCareThemeColorScheme',
  InvCustomerCareThemePrimaryColor = 'InvCustomerCareThemePrimaryColor',
  InvCustomerCareThemeSecondaryColor = 'InvCustomerCareThemeSecondaryColor',
  InvDisableAutoHideForErrorToastMessage = 'InvDisableAutoHideForErrorToastMessage',
  InvPhysicalInventoryAvailabilityDisplayMode = 'InvPhysicalInventoryAvailabilityDisplayMode',
  InvPhysicalInventoryAvailabilityQuantityThreshold = 'InvPhysicalInventoryAvailabilityQuantityThreshold',
  InvPhysicalInventoryLowStockThreshold = 'InvPhysicalInventoryLowStockThreshold',
  InvToastMessageAutoHideDuration = 'InvToastMessageAutoHideDuration',
  InvToastMessageTypesWithAutoHideDuration = 'InvToastMessageTypesWithAutoHideDuration',
  InvDoNotShowOrderVerificationHooksModalOnError = 'InvDoNotShowOrderVerificationHooksModalWhenOnlyOneHook'
}

export enum PhysicalInventoryAvailabilityDisplayMode {
  StatusOnly = 'StatusOnly',
  QuantityBelowThreshold = 'QuantityBelowThreshold',
  Tiered = 'Tiered'
}

export const FeatureToggleType = {
  ...APIFeatureToggle,
  ...UIFeatureToggle,
  ...UICustomizationFeatureToggle
};

export type FeatureToggleDataType = keyof typeof FeatureToggleType;

export enum CustomizationValueDataType {
  Text = 'Text',
  Number = 'Number',
  Boolean = 'Boolean',
  Select = 'Select',
  MultiSelect = 'MultiSelect'
}

type BaseCustomizationValueConfig = {
  key: UICustomizationValue;
  label: string;
  description?: string;
  /** Section title on the template configuration screen. */
  group: string;
};

export type CustomizationValueConfig =
  | (BaseCustomizationValueConfig & {
      type: CustomizationValueDataType.Text;
      value?: string;
    })
  | (BaseCustomizationValueConfig & {
      type: CustomizationValueDataType.Number;
      value?: number;
    })
  | (BaseCustomizationValueConfig & {
      type: CustomizationValueDataType.Boolean;
      value?: boolean;
    })
  | (BaseCustomizationValueConfig & {
      type: CustomizationValueDataType.Select;
      value?: string;
      options?: LabelValuePair[];
    })
  | (BaseCustomizationValueConfig & {
      type: CustomizationValueDataType.MultiSelect;
      value?: string[];
      options?: LabelValuePair[];
    });
